import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { resolveVirtualTourEmbedUrl } from '../../super-picker/services/api';

export interface VirtualTourDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string | null;
}

/**
 * Large iframe dialog for project virtual tours (IVT / 360 links).
 * Footer includes a copy-link control so agents can share the URL
 * without leaving the app.
 */
export default function VirtualTourDialog({
  open,
  onClose,
  title,
  url,
}: VirtualTourDialogProps) {
  const [copied, setCopied] = useState(false);
  const [embedSrc, setEmbedSrc] = useState<string | null>(null);
  const [embedReady, setEmbedReady] = useState(false);
  const href = (url || '').trim();

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setEmbedSrc(null);
      setEmbedReady(false);
      return;
    }
    if (!href) {
      setEmbedSrc(null);
      setEmbedReady(true);
      return;
    }
    let cancelled = false;
    setEmbedSrc(null);
    setEmbedReady(false);
    void resolveVirtualTourEmbedUrl(href).then((next) => {
      if (cancelled) return;
      setEmbedSrc(next);
      setEmbedReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [open, href]);

  const handleCopy = async () => {
    const href = (url || '').trim();
    if (!href) return;
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — no-op.
    }
  };

  const placeholder = !href
    ? 'Virtual tour link is unavailable.'
    : embedReady
      ? 'Preview not available'
      : 'Loading tour…';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '95vw' },
          maxWidth: { xs: '100%', sm: '95vw' },
          height: { xs: '100%', sm: '92vh' },
          maxHeight: { xs: '100%', sm: '92vh' },
          m: { xs: 0, sm: 1.5 },
          borderRadius: { xs: 0, sm: 2 },
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 6,
          py: 1.5,
          fontWeight: 700,
          color: 'var(--color-text-strong)',
          flexShrink: 0,
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <Typography
          component="span"
          variant="inherit"
          noWrap
          title={title}
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title || 'Virtual tour'}
        </Typography>
      </DialogTitle>
      <IconButton
        onClick={onClose}
        aria-label="Close virtual tour"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'var(--color-text-muted)',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#0b1220',
        }}
      >
        {embedSrc ? (
          <Box
            component="iframe"
            src={embedSrc}
            title={title || 'Virtual tour'}
            allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope; magnetometer"
            allowFullScreen
            sx={{
              flex: 1,
              width: '100%',
              minHeight: 0,
              border: 0,
              bgcolor: '#000',
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1, p: 3, textAlign: 'center' }}
          >
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {placeholder}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="flex-end"
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          flexShrink: 0,
          borderTop: '1px solid var(--color-border)',
          bgcolor: 'var(--color-surface)',
        }}
      >
        <Button
          onClick={handleCopy}
          disabled={!href}
          variant="outlined"
          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 999,
            borderColor: copied ? '#22c55e' : 'var(--color-border-strong)',
            color: copied ? '#22c55e' : 'var(--color-text-strong)',
            '&:hover': {
              borderColor: 'var(--color-accent)',
              bgcolor: 'var(--color-accent-soft)',
              color: 'var(--color-accent-strong)',
            },
          }}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </Stack>
    </Dialog>
  );
}
